import React, { useState, useEffect } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';

// Importa Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  // Verificar si hay sesión activa al cargar
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('admin_token');
      const email = localStorage.getItem('admin_email');
      if (token && email) {
        setIsAuthenticated(true);
        setUserEmail(email);
      } else {
        setIsAuthenticated(false);
        setUserEmail('');
      }
    };

    // Verificar al cargar
    checkAuth();

    // Escuchar cambios en localStorage
    const handleStorageChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);

    // También podemos escuchar eventos personalizados
    window.addEventListener('authChange', checkAuth);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', checkAuth);
    };
  }, []);

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Vacantes', href: '/vacantes' },
    { name: 'Tienda Packsys', href: 'https://packsys.com/', external: true },
  ];

  const contactLinks = [
    {
      id: 'cdmx',
      name: 'CDMX',
      href: 'https://wa.me/525588693920?text=¡Hola%20me%20gustaría%20ser%20parte%20del%20equipo%20Packsys!',
      icon: 'whatsapp',
      label: 'WhatsApp',
      phone: '55 88 69 39 20',
      gradient: 'from-green-500 to-emerald-600',
      hoverGradient: 'from-green-600 to-emerald-700',
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    setIsAuthenticated(false);
    setUserEmail('');
    navigate('/');
  };

  const goToAdminPanel = () => {
    navigate('/admin');
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed w-full z-50 transition-all duration-500 bg-white/95 backdrop-blur-xl shadow-2xl py-2`}
      >
        <nav className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4 group">
              <div className="relative">
                <img
                  src={'/assets/logo/logo.png'}
                  alt="Packsys"
                  className="h-16 w-auto transition-all duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-orange/0 to-primary-blue/0 group-hover:from-primary-orange/10 group-hover:to-primary-blue/10 rounded-full transition-all duration-300 blur-sm"></div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-10">
              {navigation.map((item) =>
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`relative font-semibold text-lg transition-all duration-300 hover:text-primary-orange group text-gray-800`}
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-orange to-primary-blue transition-all duration-300 group-hover:w-full"></span>
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`relative font-semibold text-lg transition-all duration-300 hover:text-primary-orange group text-gray-800 `}
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-orange to-primary-blue transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )
              )}

              {/* WhatsApp Buttons */}
              <div className="flex items-center space-x-4">
                {contactLinks.map((contact) => (
                  <a
                    key={contact.id}
                    href={contact.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                  >
                    <div className="flex flex-col items-center transition-all duration-300 transform hover:-translate-y-1">
                      <div
                        className={`relative overflow-hidden rounded-2xl p-4 
                      bg-gradient-to-br ${contact.gradient} 
                      hover:bg-gradient-to-br ${contact.hoverGradient}
                      shadow-2xl hover:shadow-3xl
                      transition-all duration-500
                      min-w-[140px]
                      flex items-center justify-center space-x-3`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                        {/* Icono de Font Awesome */}
                        <div className="relative z-10">
                          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                            <FontAwesomeIcon
                              icon={faWhatsapp}
                              className="text-white text-xl"
                            />
                          </div>
                        </div>

                        <div className="text-left">
                          <div className="text-white font-bold text-lg leading-tight">
                            {contact.label}
                          </div>
                        </div>

                        <svg
                          className="w-5 h-5 text-white/80 transform transition-transform duration-300 group-hover:translate-x-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/0 to-white/0 group-hover:from-white/5 group-hover:to-white/5 transition-all duration-500 opacity-0 group-hover:opacity-100 -z-10 blur-md"></div>
                  </a>
                ))}
              </div>

              {/* Admin Section - SOLO se muestra si está autenticado */}
              {isAuthenticated && (
                <div className="ml-8">
                  <div className="relative group">
                    <button className="flex items-center space-x-3 px-4 py-2.5 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white rounded-xl hover:shadow-lg transition-all duration-300">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <span className="font-medium">
                        {localStorage.getItem('admin_name') ||
                          userEmail.split('@')[0]}
                      </span>
                      <svg
                        className="w-4 h-4 transform group-hover:rotate-180 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <div className="p-4 border-b border-gray-100">
                        <p className="text-sm text-gray-500">
                          Sesión activa como
                        </p>
                        <p className="font-medium text-gray-900 truncate">
                          {userEmail}
                        </p>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={goToAdminPanel}
                          className="flex items-center w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <svg
                            className="w-5 h-5 mr-3 text-primary-blue"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          Panel de administración
                        </button>
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-1"
                        >
                          <svg
                            className="w-5 h-5 mr-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              className="lg:hidden p-3 rounded-xl bg-white/10 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-7 w-7 text-gray-700" />
              ) : (
                <Bars3Icon className="h-7 w-7 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile menu */}
          <div
            className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
              mobileMenuOpen
                ? 'max-h-[800px] opacity-100 mt-2'
                : 'max-h-0 opacity-0 mt-0'
            }`}
          >
            <div className="border-t border-gray-200 pt-6 pb-8">
              {/* Navigation Links */}
              <div className="space-y-4 mb-8">
                {navigation.map((item) =>
                  item.external ? (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="font-semibold text-gray-800 text-lg">
                        {item.name}
                      </span>
                      <svg
                        className="w-5 h-5 text-gray-400 group-hover:text-primary-orange transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="group flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="font-semibold text-gray-800 text-lg">
                        {item.name}
                      </span>
                      <svg
                        className="w-5 h-5 text-gray-400 group-hover:text-primary-orange transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  )
                )}

                {/* Admin en móvil - SOLO si está autenticado */}
                {isAuthenticated && (
                  <div className="py-3 px-4 rounded-xl bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 rounded-full flex items-center justify-center mr-3">
                          <svg
                            className="w-5 h-5 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {localStorage.getItem('admin_name')}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {userEmail}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={goToAdminPanel}
                        className="px-4 py-2 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white text-sm font-medium rounded-lg hover:shadow-md transition-all"
                      >
                        Panel Admin
                      </button>
                      <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-lg border border-red-200 hover:bg-red-100 transition-colors"
                      >
                        Cerrar sesión
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* WhatsApp Buttons */}
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4 px-2">
                  Contáctanos ahora
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {contactLinks.map((contact) => (
                    <a
                      key={contact.id}
                      href={contact.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`
                      block rounded-2xl p-5 
                      bg-gradient-to-br ${contact.gradient}
                      hover:bg-gradient-to-br ${contact.hoverGradient}
                      shadow-xl hover:shadow-2xl
                      transition-all duration-300 transform hover:-translate-y-1
                      group`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <FontAwesomeIcon
                              icon={faWhatsapp}
                              className="text-white text-2xl"
                            />
                          </div>
                          <div>
                            <div className="text-white font-bold text-lg">
                              {contact.label}
                            </div>
                          </div>
                        </div>
                        <svg
                          className="w-6 h-6 text-white/80 transform transition-transform duration-300 group-hover:translate-x-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;
